import { IconButton } from "@mui/material";
import { UseMutationResult } from "@tanstack/react-query";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useRecoilState } from "recoil";
import DeleteIcon from "src/assets/svg-icons/aggregators/delete.svg";
import { ConfirmPopup } from "src/shared/components/confirm-popup";
import { notificationsState } from "src/store/notifications";
import { APIErrorResponse } from "src/types/generic";

/**
 * Delete action for a single row of any salary payment table (payments,
 * event deductions, owners payments). The table owns the mutation; this only
 * confirms, fires it with the row's variables and reports the result.
 */
function DeleteRowAction<TVariables>({
  item,
  mutation,
  variables,
  refetch,
}: {
  item: string;
  mutation: UseMutationResult<any, APIErrorResponse, TVariables>;
  variables: TVariables;
  refetch: () => void;
}) {
  const { t: generalT } = useTranslation("general");
  const [notifications, setNotifications] = useRecoilState(notificationsState);
  const [confirmOpen, setConfirmOpen] = useState(false);

  const onConfirm = () => {
    mutation.mutate(variables, {
      onSuccess: () => {
        setConfirmOpen(false);
        refetch();
        setNotifications([
          ...notifications,
          { type: "success", message: generalT("deletedSuccessfully") },
        ]);
      },
      onError: (err: APIErrorResponse) => {
        setNotifications([
          ...notifications,
          {
            type: "error",
            message: err?.data?.errors?.[0]?.value ?? generalT("somethingWrong"),
          },
        ]);
      },
    });
  };

  return (
    <>
      <IconButton
        size="small"
        aria-label={generalT("delete")}
        onClick={() => setConfirmOpen(true)}
      >
        <img src={DeleteIcon} alt="" />
      </IconButton>

      <ConfirmPopup
        open={confirmOpen}
        handleClose={() => setConfirmOpen(false)}
        handleConfirm={onConfirm}
        isPending={mutation.isPending}
        item={item}
      />
    </>
  );
}

export default DeleteRowAction;
